import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { X } from 'lucide-react';
import { askConfirm } from './dialogs';

// Lehké UI primitivy v Tailwindu — antracitový minimalismus (viz větev design).

export const cn = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md';
};

const buttonVariants: Record<NonNullable<ButtonProps['variant']>, string> = {
  // Akcent řídí motiv (viz lib/theme.ts). V neutrálním motivu = původní antracit.
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
  secondary:
    'border border-zinc-300 bg-white hover:border-zinc-400 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600 dark:hover:bg-zinc-800',
  danger: 'bg-red-600 text-white hover:bg-red-700',
  ghost: 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-all duration-150 active:scale-[0.98]',
        'disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-zinc-950',
        size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
        buttonVariants[variant],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

// Sdílený vzhled textových polí — klidný okraj, zřetelný antracitový fokus.
// Šířka z className má přednost: dvě třídy šířky by rozhodlo pořadí v CSS, ne to, co je napsané.
const fieldWidth = (className?: string) => (/(^|\s)w-/.test(className ?? '') ? '' : 'w-full');

const fieldBase =
  'rounded-lg border border-zinc-300 bg-white text-sm transition-colors ' +
  'placeholder:text-zinc-400 hover:border-zinc-400 ' +
  'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15 ' +
  'dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input ref={ref} className={cn('h-9 px-3', fieldBase, fieldWidth(className), className)} {...props} />
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => (
    <textarea ref={ref} className={cn('px-3 py-2', fieldBase, fieldWidth(className), className)} {...props} />
  ),
);
Textarea.displayName = 'Textarea';

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, ...props }, ref) => (
    <select ref={ref} className={cn('h-9 px-2.5', fieldBase, fieldWidth(className), className)} {...props} />
  ),
);
Select.displayName = 'Select';

export const Checkbox = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      type="checkbox"
      className={cn(
        'h-4 w-4 rounded border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900',
        'text-zinc-900 dark:text-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
        className,
      )}
      {...props}
    />
  ),
);
Checkbox.displayName = 'Checkbox';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-zinc-200/70 dark:bg-zinc-800/70', className)} />;
}

export function Progress({
  value,
  className,
  tone = 'emerald',
}: {
  value: number;
  className?: string;
  tone?: 'emerald' | 'amber' | 'zinc';
}) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const bar = tone === 'amber' ? 'bg-amber-500' : tone === 'zinc' ? 'bg-zinc-400' : 'bg-emerald-500';
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800', className)}>
      <div className={cn('h-full rounded-full transition-all', bar)} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label
        htmlFor={htmlFor}
        className="block text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-500 dark:text-zinc-400"
      >
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs normal-case tracking-normal text-zinc-400">{hint}</p>}
      {error && <p className="text-xs normal-case tracking-normal text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

// Otevřená okna, poslední navrchu: Esc patří jen tomu vrchnímu.
const openModals: object[] = [];

export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const self = useRef({}).current;
  const card = useRef<HTMLDivElement>(null);
  const edited = useRef(false); // člověk něco napsal nebo vybral
  const pressedOutside = useRef(false);
  const asking = useRef(false);

  // Omylem vedle nebo Esc nesmí zahodit rozepsanou práci. Zrušit a Uložit v okně zavírají rovnou.
  const requestClose = useCallback(async () => {
    if (asking.current) return;
    if (edited.current) {
      asking.current = true;
      const discard = await askConfirm({
        title: 'Zahodit rozepsané?',
        body: 'Neuložené změny se ztratí.',
        confirmLabel: 'Zahodit',
        cancelLabel: 'Pokračovat v úpravě',
        danger: true,
      });
      asking.current = false;
      if (!discard) return;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    openModals.push(self);
    const previous = document.activeElement as HTMLElement | null;
    if (!card.current?.contains(document.activeElement)) card.current?.focus({ preventScroll: true });
    return () => {
      openModals.splice(openModals.indexOf(self), 1);
      previous?.focus?.({ preventScroll: true });
    };
  }, [self]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || openModals[openModals.length - 1] !== self) return;
      void requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [self, requestClose]);

  // Portál: okno otevřené z jiného okna nesmí zůstat v jeho rámu (animace karty by ho ořízla).
  // V body se nedědí barva textu z Layoutu, proto ji okno nastavuje samo.
  // Zavírá jen stisk i puštění mimo kartu: tažení výběru textu z pole ven okno nezavře.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 backdrop-blur-[2px] p-4 pt-[8vh] text-zinc-900 dark:text-zinc-100"
      onMouseDown={(e) => { pressedOutside.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.target === e.currentTarget && pressedOutside.current) void requestClose(); }}
    >
      {/* Karta nikdy nepřeteče obrazovku: hlavička drží, tělo formuláře roluje
          (jinak by se u dlouhých formulářů nedalo dostat na tlačítka dole). */}
      <div
        ref={card}
        tabIndex={-1}
        className={`flex w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[84vh] flex-col rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-2xl outline-none`}
        style={{ animation: 'fadeIn 160ms ease-out' }}
        onInput={() => { edited.current = true; }}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-100 dark:border-zinc-800 px-5 py-3.5">
          <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
          <button
            onClick={() => void requestClose()}
            className="grid h-7 w-7 place-items-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            aria-label="Zavřít"
          >
            <X size={16} />
          </button>
        </div>
        <div className="min-h-0 overflow-y-auto p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
